/**
 * 1.4.0 asset groups, stream G2: Add to group… in the Library's selection bar (LibrarySelectionBar.tsx, group.manage):
 * a tree picker of the existing groups with search, plus "New Series…" / "New Season in …" inline; assets already in
 * another group of the type are moved after a confirmation (409 GROUP_TYPE_TAKEN, then move: true). The same dialog
 * is the asset page's Change (one asset, one type) and the group page's Add assets target. G2's file.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, FolderTree, Layers, Loader2, Plus, Search, Tv } from 'lucide-react';
import { GROUP_ERRORS, type AssetGroupDTO, type AssetSummary, type GroupTypeDTO, type GroupTypeTakenDetails } from '@prism/contracts';
import { ApiError, errorMessage } from '../../api/client';
import { useMe } from '../../api/hooks';
import { useAddGroupMembers, useCreateGroup, useGroups, useGroupTypes } from '../../api/hooks/groups';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';

const NO_TYPES: GroupTypeDTO[] = [];
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function useCanManageGroups(): boolean {
  return useMe().data?.permissions.includes('group.manage') ?? false;
}

/** The type's icon, as the Group types admin shows it. */
export function GroupTypeIcon({ icon, size = 14 }: { icon: string | null | undefined; size?: number }) {
  return icon === 'tv' ? <Tv size={size} /> : <Layers size={size} />;
}

type Choice = { kind: 'group'; group: AssetGroupDTO } | { kind: 'new'; type: GroupTypeDTO; parent: AssetGroupDTO | null };

/** One level of the tree: the groups under `parent` (root: the top-level groups), each with its children on demand. */
function Level({ parent, depth, types, pick, onPick, typeId }: {
  parent: AssetGroupDTO | null; depth: number; types: GroupTypeDTO[]; pick: Choice | null; onPick: (p: Choice) => void; typeId?: string;
}) {
  const q = useGroups({ parent: parent ? parent.id : 'root' });
  const items = q.data?.items ?? [];
  const childTypes = (t: string | null) => types.filter((x) => (x.parentTypeId ?? null) === t && !x.archived);
  // With one type (the asset page's Change), only a new group of that type is offered; its parents open as nodes.
  const newTypes = childTypes(parent ? parent.typeId : null).filter((t) => !typeId || t.id === typeId);
  return (
    <>
      {q.isPending && <li className="faint group-pick-row" style={{ paddingLeft: 10 + depth * 18 }}><Loader2 size={12} className="spin" /> Loading…</li>}
      {items.map((g) => <Node key={g.id} g={g} depth={depth} types={types} pick={pick} onPick={onPick} typeId={typeId} />)}
      {q.hasMore && <li style={{ paddingLeft: 10 + depth * 18 }}><button className="btn ghost sm" onClick={q.loadMore} disabled={q.loadingMore}>Load more</button></li>}
      {newTypes.map((t) => {
        const on = pick?.kind === 'new' && pick.type.id === t.id && (pick.parent?.id ?? null) === (parent?.id ?? null);
        return (
          <li key={`new-${t.id}`} className={`group-pick-row new ${on ? 'selected' : ''}`} style={{ paddingLeft: 10 + depth * 18 }}>
            <button type="button" className="group-pick-new" onClick={() => onPick({ kind: 'new', type: t, parent })} data-testid="group-pick-new" data-type={t.key}>
              <Plus size={13} /> New {t.label}{parent ? ` in ${parent.title}` : ''}…
            </button>
          </li>
        );
      })}
    </>
  );
}

/** Whether `ancestor` is somewhere above `typeId` in the types' chain (a Series above a Season). */
function isAncestorType(types: GroupTypeDTO[], ancestor: string, typeId: string): boolean {
  let cur = types.find((t) => t.id === typeId)?.parentTypeId ?? null;
  for (let hops = 0; cur && hops < 32; hops++) {
    if (cur === ancestor) return true;
    cur = types.find((t) => t.id === cur)?.parentTypeId ?? null;
  }
  return false;
}

function Node({ g, depth, types, pick, onPick, typeId }: { g: AssetGroupDTO; depth: number; types: GroupTypeDTO[]; pick: Choice | null; onPick: (p: Choice) => void; typeId?: string }) {
  const [open, setOpen] = useState(false);
  const type = types.find((t) => t.id === g.typeId);
  const hasChildTypes = types.some((t) => t.parentTypeId === g.typeId && !t.archived && (!typeId || t.id === typeId || isAncestorType(types, t.id, typeId)));
  const selectable = !typeId || g.typeId === typeId;
  const on = pick?.kind === 'group' && pick.group.id === g.id;
  return (
    <>
      <li className={`group-pick-row ${on ? 'selected' : ''}`} style={{ paddingLeft: 10 + depth * 18 }} data-testid="group-pick-row" data-title={g.title}>
        {hasChildTypes
          ? <button type="button" className="btn ghost icon sm" aria-expanded={open} aria-label={`${open ? 'Close' : 'Open'} ${g.title}`} onClick={() => setOpen(!open)} data-testid="group-pick-expand">
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
          : <span className="group-pick-spacer" />}
        <button type="button" className="group-pick-name" disabled={!selectable} aria-pressed={on} onClick={() => onPick({ kind: 'group', group: g })}>
          <GroupTypeIcon icon={type?.icon} size={13} />
          <span>{g.title}</span>
          <span className="faint">{g.type.label}{g.number !== null && g.type.numberLabel ? ` ${g.number}` : ''} · {plural(g.memberCount, g.type.memberLabel.toLowerCase())}</span>
        </button>
      </li>
      {open && <Level parent={g} depth={depth + 1} types={types} pick={pick} onPick={onPick} typeId={typeId} />}
    </>
  );
}

function SearchResults({ q, pick, onPick, typeId }: { q: string; pick: Choice | null; onPick: (p: Choice) => void; typeId?: string }) {
  const r = useGroups({ q, type: typeId });
  const items = r.data?.items ?? [];
  return (
    <>
      {r.isPending && <li className="faint group-pick-row"><Loader2 size={12} className="spin" /> Searching…</li>}
      {items.map((g) => (
        <li key={g.id} className={`group-pick-row ${pick?.kind === 'group' && pick.group.id === g.id ? 'selected' : ''}`} data-testid="group-pick-row" data-title={g.title}>
          <span className="group-pick-spacer" />
          <button type="button" className="group-pick-name" onClick={() => onPick({ kind: 'group', group: g })}>
            <span>{g.title}</span><span className="faint">{g.type.label} · {plural(g.memberCount, g.type.memberLabel.toLowerCase())}</span>
          </button>
        </li>
      ))}
      {!r.isPending && !items.length && <li className="faint group-pick-row">No group matches “{q}”.</li>}
      {r.hasMore && <li><button className="btn ghost sm" onClick={r.loadMore}>Load more</button></li>}
    </>
  );
}

/**
 * The picker and the add: `assets` go into the picked group (or a new one). typeId limits the picker to one type (the
 * asset page's Change). onDone gets the group they went into.
 */
export function AddToGroupDialog({ assets, typeId, title, onClose, onDone }: {
  assets: Array<Pick<AssetSummary, 'id' | 'title' | 'assetCode'>>; typeId?: string; title?: string; onClose: () => void; onDone?: (groupId: string) => void;
}) {
  const allTypes = useGroupTypes().data?.items ?? NO_TYPES;
  const [search, setSearch] = useState('');
  const [pick, setPick] = useState<Choice | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [taken, setTaken] = useState<GroupTypeTakenDetails['assets'] | null>(null);
  const add = useAddGroupMembers();
  const create = useCreateGroup();
  const busy = add.isPending || create.isPending;
  const ids = assets.map((a) => a.id);
  const target = pick?.kind === 'group' ? pick.group.title : pick?.kind === 'new' ? (newTitle.trim() || `the new ${pick.type.label}`) : null;

  async function go(move = false) {
    if (!pick) return;
    try {
      let groupId: string;
      let moved = 0;
      let refused: Array<{ assetId: string; message: string }> = [];
      if (pick.kind === 'group') {
        const r = await add.mutateAsync({ id: pick.group.id, assetIds: ids, move });
        groupId = pick.group.id;
        moved = r.moved.length;
        refused = r.refused;
        toast(`${plural(r.added.length + r.moved.length, 'asset')} added to ${pick.group.title}${moved ? ` (${moved} moved from another ${pick.group.type.label.toLowerCase()})` : ''}`);
      } else {
        const n = newNumber.trim() === '' ? null : Number(newNumber);
        const g = await create.mutateAsync({ typeId: pick.type.id, parentId: pick.parent?.id ?? null, title: newTitle.trim(), number: Number.isFinite(n) ? n : null, assetIds: ids, move });
        groupId = g.id;
        toast(`${g.type.label} ${g.title} created with ${plural(g.memberCount, 'asset')}`);
      }
      if (refused.length) toast(`Not added: ${refused.map((x) => `${assets.find((a) => a.id === x.assetId)?.assetCode ?? x.assetId} (${x.message})`).join('; ')}`, 'error');
      onDone?.(groupId);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === GROUP_ERRORS.GROUP_TYPE_TAKEN && !move) {
        setTaken(((err.details as unknown as GroupTypeTakenDetails | undefined)?.assets) ?? []);
        return;
      }
      toast(errorMessage(err), 'error');
    }
  }

  const numbered = pick?.kind === 'new' && pick.type.numberLabel;
  const canGo = Boolean(pick) && !busy && (pick?.kind !== 'new' || Boolean(newTitle.trim()));
  if (taken) {
    const typeLabel = pick?.kind === 'group' ? pick.group.type.label : pick?.kind === 'new' ? pick.type.label : 'group';
    return (
      <Modal title={`Move to ${target}?`} onClose={() => setTaken(null)} actions={<>
        <button className="btn ghost" onClick={() => setTaken(null)}>Back</button>
        <button className="btn primary" disabled={busy} onClick={() => { setTaken(null); void go(true); }} data-testid="group-move-confirm">Move {taken.length || ''}</button>
      </>}>
        <div data-testid="group-move-dialog">
          <p style={{ marginTop: 0 }}>An asset is in one {typeLabel.toLowerCase()} at a time. {taken.length === 1 ? 'This asset is' : 'These assets are'} already in another {typeLabel.toLowerCase()} and will move:</p>
          <ul className="group-move-list">
            {taken.map((t) => {
              const a = assets.find((x) => x.id === t.assetId);
              return <li key={t.assetId}><span className="mono">{a?.assetCode}</span> {a?.title ?? t.assetId} <span className="faint">· now in {t.title}</span></li>;
            })}
          </ul>
        </div>
      </Modal>
    );
  }
  return (
    <Modal title={title ?? `Add ${plural(assets.length, 'asset')} to a group`} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!canGo} onClick={() => void go(false)} data-testid="group-pick-save">
        {busy ? <Loader2 size={14} className="spin" /> : <FolderTree size={14} />} {target ? `${pick?.kind === 'new' ? 'Create and add to' : 'Add to'} ${target}` : 'Add'}
      </button>
    </>}>
      <div className="group-picker" data-testid="group-picker">
        <div className="group-picker-search">
          <Search size={14} className="faint" />
          <input className="input" type="search" placeholder="Search groups" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search groups" data-testid="group-pick-search" />
        </div>
        <ul className="group-pick-tree" role="tree" aria-label="Groups">
          {search.trim()
            ? <SearchResults q={search.trim()} pick={pick} onPick={setPick} typeId={typeId} />
            : <Level parent={null} depth={0} types={allTypes} pick={pick} onPick={setPick} typeId={typeId} />}
        </ul>
        {pick?.kind === 'new' && (
          <div className="group-picker-new" data-testid="group-pick-new-form">
            <div className="field" style={{ flex: 2 }}>
              <label htmlFor="gp-title">{pick.type.label} title{pick.parent ? ` in ${pick.parent.title}` : ''}</label>
              <input id="gp-title" className="input" autoFocus value={newTitle} maxLength={300} onChange={(e) => setNewTitle(e.target.value)}
                placeholder={pick.parent ? `e.g. ${pick.type.label} 1` : 'e.g. American Horror Story'} data-testid="group-pick-new-title"
                onKeyDown={(e) => { if (e.key === 'Enter' && canGo) void go(false); }} />
            </div>
            {numbered && (
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="gp-number">{pick.type.numberLabel}</label>
                <input id="gp-number" className="input" type="number" min={0} value={newNumber} onChange={(e) => setNewNumber(e.target.value)} data-testid="group-pick-new-number" />
              </div>
            )}
          </div>
        )}
        <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>
          {assets.slice(0, 5).map((a) => a.assetCode).join(', ')}{assets.length > 5 ? ` and ${assets.length - 5} more` : ''}
        </p>
      </div>
    </Modal>
  );
}

export function AddToGroupSelectionButton({ selected }: { selected: AssetSummary[] }) {
  const can = useCanManageGroups();
  const [open, setOpen] = useState(false);
  if (!can) return null;
  return (
    <>
      <button className="btn ghost sm" onClick={() => setOpen(true)} data-testid="selection-add-to-group" disabled={!selected.length}
        title="Put the selected assets into a series, a season or another group">
        <FolderTree size={13} /> Add {selected.length} to group…
      </button>
      {open && <AddToGroupDialog assets={selected} onClose={() => setOpen(false)} />}
    </>
  );
}
